import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Link,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BrandsContext } from "contexts/Brands";
import { forwardRef, useContext, useState } from "react";
import { BentoBrand, LOCATIONS, SearchParams } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { DiscoverSearchChipColor } from "utils/color";
import { generateInitials } from "utils/string";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

interface SelectedBrandProps {
  bentoBrand: BentoBrand;
  handleSelect: (bentoBrand: BentoBrand) => void;
  isSelected: boolean;
}

const SelectedBrand = forwardRef(
  ({ bentoBrand, handleSelect, isSelected }: SelectedBrandProps, ref) => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const { requestRecategorization } = useContext(BrandsContext);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [imgError, setImgError] = useState<boolean>(false);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const goToInstagram = (e: CustomEvent) => {
      e.stopPropagation();
      trackEvent("Instagram Link Viewed", {
        "Instagram Username": bentoBrand.instagramUsername,
      });
      window.open(
        `https://instagram.com/${bentoBrand.instagramUsername}`,
        "_blank",
      );
    };

    const matchedLocation = LOCATIONS?.find(
      (x) => x.key === bentoBrand?.countryCode?.toLowerCase(),
    );
    const location = matchedLocation
      ? matchedLocation?.name
      : bentoBrand?.countryCode;

    const punctuationMatch = bentoBrand?.aboutSummary?.match(/[^.!?]+[.!?]+/g);
    const firstSentence = punctuationMatch?.[0];

    const handleRecategorize = async (e: CustomEvent) => {
      e.stopPropagation();
      requestRecategorization(bentoBrand.id);
    };

    return (
      <Box
        ref={ref}
        sx={[styles.brandContainer, isSelected && styles.selected]}
        key={`cart-brand-${bentoBrand.id}`}
        onClick={() => {
          handleSelect(bentoBrand);
        }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <Box sx={styles.brandImage}>
          {!imgError && bentoBrand.logoUrl ? (
            <img
              src={bentoBrand.logoUrl}
              alt={`${bentoBrand.normalizedName} logo`}
              onError={() => setImgError(true)}
            />
          ) : (
            <Avatar sx={styles.avatar}>
              {generateInitials(bentoBrand.normalizedName)}
            </Avatar>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={styles.brandName}>{bentoBrand.normalizedName}</Box>

          {bentoBrand.instagramUsername && (
            <Link target="_blank" onClick={goToInstagram} sx={{ fontSize: 12 }}>
              @{bentoBrand.instagramUsername}
            </Link>
          )}
        </Box>

        <Tooltip title="Request recategorization">
          <IconButton sx={{ fontSize: 12 }} onClick={handleRecategorize}>
            <Box component="i" className="fa-regular fa-refresh" />
          </IconButton>
        </Tooltip>

        <Box onClick={(e) => e.stopPropagation()}>
          <Checkbox
            onChange={() => {
              handleSelect(bentoBrand);
            }}
            disableRipple
            checked={isSelected}
            icon={
              <Box
                sx={{ color: "green.main" }}
                component="i"
                className="fa-regular fa-square"
              />
            }
            checkedIcon={
              <Box
                component="i"
                className="fa-solid fa-square-check"
                sx={{ color: "green.main" }}
              />
            }
          />
        </Box>

        {!isMobileScreen && (
          <Popover
            id={`brand-popover-${bentoBrand.id}`}
            sx={{ pointerEvents: "none" }}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Box sx={{ p: 1, maxWidth: 300 }}>
              <Grid container item xs={12} gap={1}>
                {location && (
                  <Chip
                    sx={{
                      backgroundColor:
                        DiscoverSearchChipColor[SearchParams.LOCATION_DISCOVER],
                      fontSize: 10,
                    }}
                    size="small"
                    label={location}
                  />
                )}
                {[
                  ...(bentoBrand?.mainCategoryNames || []),
                  ...(bentoBrand?.secondaryCategoryNames || []),
                ]
                  ?.slice(0, 2)
                  .map((category, index) => (
                    <Chip
                      sx={{
                        backgroundColor:
                          DiscoverSearchChipColor[
                            SearchParams.CATEGORY_ID_DISCOVER
                          ],
                        fontSize: 10,
                      }}
                      size="small"
                      label={category}
                      key={index}
                    />
                  ))}
              </Grid>
              <Typography sx={{ fontSize: 10 }}>{firstSentence}</Typography>
            </Box>
          </Popover>
        )}
      </Box>
    );
  },
);

export default SelectedBrand;
